import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { NAV } from "@/constants/testIds/landing";
import { Button } from "@/components/ui/button";
import { getContextualBundleCta } from "@/lib/userRole";
import TrackedPhoneLink from "@/components/site/TrackedPhoneLink";
import { COURSE_LIST } from "@/data/courses";
import { FEATURED_COURSE_IDS } from "@/data/featuredCourses";
import { PwaInstallHeaderButton } from "@/components/site/PwaInstall";

const LOGO_ICON = "/dh_logo_dark.webp";

const links = [
  { id: 'kurse', label: 'Weiterbildungen', tid: NAV.linkKurse, hasDropdown: true },
  { id: 'pflegekraefte', label: 'Für Pflegekräfte', tid: NAV.linkPflegekraft, external: true },
  { id: 'pflegedienste', label: 'Für Einrichtungen', tid: NAV.linkEinrichtung, external: true },
  { id: 'services', label: 'Services', tid: 'nav-link-services', external: true },
  { id: 'pflichtfortbildungen', label: 'Pflichtfortbildungen', tid: 'nav-link-pflicht', external: true },
  { id: 'leitfaeden', label: 'Leitfäden', tid: 'nav-link-leitfaden', external: true },
  { id: 'shop', label: 'Shop', tid: 'nav-link-shop', external: true },
  { id: 'campus', label: 'Campus', tid: 'nav-link-campus' },
];

// Sekundäre Links – nur im Mobile-Menü sichtbar, sonst über Footer erreichbar
const secondaryLinks = [
  { id: 'team', label: 'Dozentinnen' },
  { id: 'faq', label: 'FAQ' },
  { id: 'kontakt', label: 'Kontakt' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [mobileCoursesOpen, setMobileCoursesOpen] = useState(false);
  const [langbarOffset, setLangbarOffset] = useState(0);
  const [stripeCta, setStripeCta] = useState(() => getContextualBundleCta());
  const location = useLocation();
  const coursesTimeout = useRef(null);

  const popularCourses = FEATURED_COURSE_IDS
    .map((id) => COURSE_LIST.find((c) => c.id === id))
    .filter(Boolean);

  // Cinematic Hero: nur der Shop hat noch einen dunklen Kino-Hero.
  // Kursseiten sind seit dem Desenio-Redesign hell → Header immer solid.
  const cinemaTop = location.pathname === "/shop";
  const transparent = cinemaTop && !scrolled && !open && !coursesOpen;

  const handleCoursesEnter = () => {
    if (coursesTimeout.current) clearTimeout(coursesTimeout.current);
    setCoursesOpen(true);
  };
  const handleCoursesLeave = () => {
    coursesTimeout.current = setTimeout(() => setCoursesOpen(false), 150);
  };

  // Re-read role on route change so the stripe immediately reflects a fresh funnel submit.
  useEffect(() => {
    setStripeCta(getContextualBundleCta());
  }, [location.pathname]);

  useEffect(() => {
    const measure = () => {
      // Berücksichtige alle sichtbaren Pre-Header-Bars (Announcement + LangBar)
      const announce = document.querySelector('[data-testid="announcement-bar"]');
      const bar = document.querySelector('[data-testid="lang-bar"]');
      const aBottom = announce ? Math.max(0, announce.getBoundingClientRect().bottom) : 0;
      const lBottom = bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 0;
      setLangbarOffset(Math.max(aBottom, lBottom));
      setScrolled(window.scrollY > 8);
    };
    measure();
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <header
      style={{ top: langbarOffset }}
      className={`fixed inset-x-0 z-50 transition-all duration-300 border-b ${
        transparent
          ? 'bg-transparent border-transparent'
          : scrolled ? 'bg-cream-50/90 backdrop-blur-xl border-sand-800/15' : 'bg-cream-50 border-sand-800/15'
      }`}
    >
      {/* TRUST-STRIPE entfernt (Feb 2026 · v6-Simplify): Die rotierende
          AnnouncementBar deckt beide Botschaften (Externenprüfung + Pflege-KI)
          bereits ab. Ein zweiter Bordeaux-Streifen wirkte redundant und
          drückte den Nav-Header nach unten. */}

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20 md:h-[5.5rem]">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-4 group shrink-0 mr-8 2xl:mr-12">
          <img
            src={LOGO_ICON}
            alt="Dickies-Campus"
            className={`h-14 md:h-[4.2rem] w-auto object-contain transition-all duration-500 group-hover:rotate-[-4deg] group-hover:scale-105 ${transparent ? 'brightness-0 invert' : ''}`}
          />
          <span className={`hidden sm:block w-px h-7 ${transparent ? 'bg-cream-50/35' : 'bg-sand-800/15'}`} />
          <span className="hidden sm:block leading-none">
            <span className={`block text-[13px] tracking-[0.28em] uppercase font-semibold ${transparent ? 'text-cream-50' : 'text-sand-800'}`}>
              Dickies-Campus
            </span>
            <span className={`block mt-1.5 text-[8.5px] uppercase tracking-[0.32em] font-semibold ${transparent ? 'text-brand-rose' : 'text-terra'}`}>
              Akademie für Pflege
            </span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
          {links.map((l) => (
            l.hasDropdown ? (
              <div
                key={l.id}
                className="relative"
                onMouseEnter={handleCoursesEnter}
                onMouseLeave={handleCoursesLeave}
              >
                <Link
                  to={`/#${l.id}`}
                  data-testid={l.tid}
                  className={`inline-flex items-center gap-1 whitespace-nowrap text-[13px] 2xl:text-[13.5px] font-medium transition-colors relative group ${transparent ? 'text-cream-100/90 hover:text-cream-50' : 'text-sand-600 hover:text-terra'}`}
                >
                  {l.label}
                  <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} />
                  <span className={`absolute -bottom-1.5 left-0 h-px w-0 transition-all duration-300 group-hover:w-full ${transparent ? 'bg-brand-rose' : 'bg-terra'}`} />
                </Link>
                {coursesOpen && (
                  <div
                    data-testid="header-courses-dropdown"
                    className="absolute left-0 top-full pt-3 w-[340px]"
                  >
                    <div className="bg-cream-50 border border-cream-300 rounded-2xl shadow-hover p-3">
                      <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-sand-500 font-semibold">
                        Beliebteste Kurse
                      </div>
                      <ul className="space-y-0.5">
                        {popularCourses.map((c) => (
                          <li key={c.id}>
                            <Link
                              to={`/kurse/${c.id}`}
                              data-testid={`header-course-${c.id}`}
                              onClick={() => setCoursesOpen(false)}
                              className="block px-3 py-2.5 rounded-xl text-[13.5px] text-sand-700 hover:bg-cream-200 hover:text-sand-800 transition-colors"
                            >
                              <div className="font-medium">{c.title}</div>
                              <div className="text-[11.5px] text-sand-500 mt-0.5">{c.price} · {c.weeks}</div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                      <div className="border-t border-cream-300 mt-2 pt-2">
                        <Link
                          to="/kurse"
                          data-testid="header-courses-all"
                          onClick={() => setCoursesOpen(false)}
                          className="block px-3 py-2 rounded-xl text-[13px] text-terra font-semibold hover:bg-cream-200 transition-colors"
                        >
                          Alle Kurse ansehen →
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={l.id}
                to={l.external ? `/${l.id}` : `/#${l.id}`}
                data-testid={l.tid}
                className={`whitespace-nowrap text-[13px] 2xl:text-[13.5px] font-medium transition-colors relative group ${transparent ? 'text-cream-100/90 hover:text-cream-50' : 'text-sand-600 hover:text-terra'}`}
              >
                {l.label}
                <span className={`absolute -bottom-1.5 left-0 h-px w-0 transition-all duration-300 group-hover:w-full ${transparent ? 'bg-brand-rose' : 'bg-terra'}`} />
              </Link>
            )
          ))}
        </nav>

        {/* Compact nav zwischen lg und xl: nur wichtigste Links */}
        <nav className="hidden lg:flex xl:hidden items-center gap-5">
          {links.filter(l => ["kurse", "pflichtfortbildungen", "shop", "campus"].includes(l.id)).map((l) => (
            <Link
              key={l.id}
              to={l.external ? `/${l.id}` : `/#${l.id}`}
              data-testid={l.tid}
              className={`whitespace-nowrap text-[13px] font-medium transition-colors ${transparent ? 'text-cream-100/90 hover:text-cream-50' : 'text-sand-600 hover:text-terra'}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4 2xl:gap-5 ml-8 shrink-0">
          <a
            href="/campus/login"
            data-testid="header-campus-login"
            className={`whitespace-nowrap text-[13px] font-medium ${transparent ? 'text-cream-100/90 hover:text-cream-50' : 'text-sand-600 hover:text-sand-800'}`}
          >
            Anmelden
          </a>
          <TrackedPhoneLink
            source="header-desktop"
            data-testid="header-phone-cta"
            title="0211 29273330"
            className={`inline-flex items-center justify-center h-10 w-10 rounded-full transition-colors ${transparent ? 'text-cream-100/90 hover:text-cream-50 hover:bg-white/10' : 'text-sand-600 hover:text-sand-800 hover:bg-cream-100'}`}
            aria-label="0211 29273330"
          >
            <Phone className="h-4 w-4" strokeWidth={1.5} />
          </TrackedPhoneLink>
          <Button asChild data-testid={NAV.ctaBeratung} className={`rounded-full bg-transparent border px-6 h-11 text-[13px] font-medium uppercase tracking-[0.16em] whitespace-nowrap transition-colors shadow-none ${transparent ? 'border-brand-rose text-brand-rose hover:bg-brand-rose hover:text-espresso-deep' : 'border-terra text-terra hover:bg-terra hover:text-cream-50'}`}>
            <Link to="/#kontakt">Kostenlose Beratung</Link>
          </Button>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid={NAV.mobileToggle}
          className={`lg:hidden p-2 rounded-full border ${transparent ? 'border-cream-50/40 bg-transparent text-cream-50' : 'border-cream-300 bg-cream-50/80 text-sand-800 backdrop-blur'}`}
          aria-label="Menü öffnen"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div data-testid={NAV.mobileSheet} className="lg:hidden bg-cream-50 border-t border-cream-300">
          <nav className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              l.hasDropdown ? (
                <div key={l.id} className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => setMobileCoursesOpen((v) => !v)}
                    data-testid="mobile-courses-toggle"
                    className="flex items-center justify-between text-base font-medium text-sand-800 py-2"
                  >
                    {l.label}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${mobileCoursesOpen ? "rotate-180" : ""}`}
                      strokeWidth={2}
                    />
                  </button>
                  {mobileCoursesOpen && (
                    <div className="mt-1 ml-2 border-l-2 border-cream-300 pl-4 space-y-2">
                      {popularCourses.map((c) => (
                        <Link
                          key={c.id}
                          to={`/kurse/${c.id}`}
                          data-testid={`mobile-course-${c.id}`}
                          onClick={() => { setOpen(false); setMobileCoursesOpen(false); }}
                          className="block text-sm text-sand-700 py-1.5 hover:text-terra transition-colors"
                        >
                          {c.title}
                        </Link>
                      ))}
                      <Link
                        to="/#kurse"
                        onClick={() => { setOpen(false); setMobileCoursesOpen(false); }}
                        className="block text-sm text-terra font-semibold py-1.5"
                      >
                        Alle Kurse ansehen →
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={l.id}
                  to={l.external ? `/${l.id}` : `/#${l.id}`}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-sand-800 py-2"
                >
                  {l.label}
                </Link>
              )
            ))}
            {secondaryLinks.map((l) => (
              <Link
                key={l.id}
                to={`/#${l.id}`}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-sand-800 py-2"
              >
                {l.label}
              </Link>
            ))}
            <a
              href="/campus/login"
              onClick={() => setOpen(false)}
              className="text-base font-medium text-sand-800 py-2"
            >
              Campus-Login
            </a>
            <TrackedPhoneLink
              source="header-mobile"
              data-testid="mobile-phone-cta"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-2 text-base font-medium text-sand-800 py-2 hover:text-terra transition-colors"
            >
              <Phone className="h-4 w-4" strokeWidth={1.5} /> 0211 29273330
            </TrackedPhoneLink>
            <Button asChild className="rounded-full bg-terra text-white hover:bg-terra-dark mt-2 h-12">
              <Link to="/#kontakt" onClick={() => setOpen(false)}>Kostenlose Beratung sichern</Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
